import { Grid, makeStyles, TextField } from "@material-ui/core";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import CompanyModel from "api/models/CompanyModel";
import FileService from "api/services/FileService";
import IntegrationSetup from "components/IntegrationSetup/IntegrationSetup";
import { ICompanyData } from "components/interfaces";
import DropzoneField from "components/UI/Fields/DropzoneField";
import { PageLoading } from "components/UI/Page";
import useValidations from "hooks/useValidations";
import { useEffect, useState } from "react";
import { CompanyStore, IntegrationSetupStore } from "store";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1.5rem 0",
    overflowY: "auto",
    height: "calc(100vh - 128px)",
  },
  dropzone: { margin: "2rem 0", color: theme.palette.primary.main },
  loading_card: {
    position: "absolute",
    top: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(255,255,255,0.8)",
    zIndex: 1,
    marginLeft: "-0.5rem",
  },
}));

interface IProps {
  company?: CompanyModel;
  disabled?: boolean;
}

const companyDefault = {
  name: "",
  image: "",
  software: "Mega",
  host: "",
  user: "",
  password: "",
  tenantId: "",
};

const CompanyForm = ({ company, disabled = false }: IProps) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [enableSave, setEnableSave] = useState(false);
  const [file, setFile] = useState<File | undefined>();
  const [data, setData] = useState<ICompanyData>(companyDefault);
  const { validated, validateFields, reset } = useValidations({
    name: "required",
  });

  useEffect(() => {
    reset();
    IntegrationSetupStore.resetAuthenticationError();

    const integrationSetup = company?.integrationSetup;
    const imageLogo = company?.image;

    if (imageLogo) {
      setLoading(true);
      FileService.downloadFrom(imageLogo)
        .then((fileDownloaded) => setFile(fileDownloaded))
        .finally(() => setLoading(false));
    }

    setData({
      name: company?.name || "",
      image: imageLogo || "",
      software: integrationSetup?.software || "Mega",
      host: integrationSetup?.host || "",
      user: integrationSetup?.user || "",
      password: integrationSetup?.password || "",
      tenantId: integrationSetup?.tenantId || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeInput = (ev) => {
    const { name, value } = ev.target;
    if (name && value !== undefined) {
      const newData = { ...data };
      newData[name] = value;
      setData(newData);
      if (!enableSave) setEnableSave(true);
    }
  };

  const handleChangeFile = (files: File[]) => {
    const newFile = files.length ? files[0] : undefined;
    if (newFile !== file) {
      setFile(files[0]);
      if (!enableSave) setEnableSave(true);
    }
  };

  const handleClickSave = () => {
    if (!validateFields(data, true)) return;

    setLoading(true);
    data.host = data.host.replace(/(\/$)/, "");
    return CompanyStore.createOrUpdateCompany(data.name, file)
      .then(() =>
        IntegrationSetupStore.updateIntegrationSetup({
          software: data.software,
          host: data.host,
          user: data.user,
          password: data.password,
          tenantId: data.tenantId,
        })
      )
      .then((success) => {
        if (success) setEnableSave(false);
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBlurInput = (ev) => {
    const { name, value } = ev.target;
    validateFields({ [name]: value });
  };

  return (
    <Grid container justifyContent="center" className={classes.root}>
      {loading && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          className={classes.loading_card}
        >
          <PageLoading />
        </Grid>
      )}
      <Grid container item xs={6} lg={5} spacing={2} justifyContent="center">
        <TextField
          name="name"
          label="Nome da Empresa"
          value={data.name}
          fullWidth
          margin="normal"
          disabled={disabled}
          onChange={handleChangeInput}
          onBlur={handleBlurInput}
          error={!validated.name.valid}
          helperText={validated.name.text}
        />
        {!loading && (
          <DropzoneField
            className={classes.dropzone}
            label={"Selecionar logo da empresa"}
            Icon={AddPhotoAlternateIcon as any}
            file={file}
            disabled={disabled}
            acceptedFiles={["image/*"]}
            onChange={handleChangeFile}
          />
        )}
        {/* Mantive o salvar dentro do componente IntegrationSetup para manter as rotinas que tratam os dados da Integração separadas */}
        <IntegrationSetup
          data={data}
          update={Boolean(file) || loading}
          disabled={disabled}
          enableSave={enableSave}
          onInputChange={handleChangeInput}
          onSave={handleClickSave}
        />
      </Grid>
    </Grid>
  );
};

export default CompanyForm;
